import { Trans, t } from "@lingui/macro";

import { ARBITRUM, AVALANCHE } from "config/chains";
import { USD_DECIMALS } from "config/factors";
import { SyntheticsStateContextProvider } from "context/SyntheticsStateContext/SyntheticsStateContextProvider";
import { useInfoTokens } from "domain/tokens";
import { useChainId } from "lib/chains";
import { GLP_DECIMALS, getPageTitle } from "lib/legacy";
import { expandDecimals } from "lib/numbers";
import { useTradePageVersion } from "lib/useTradePageVersion";
import useWallet from "lib/wallets/useWallet";
import { getWhitelistedV1Tokens } from "sdk/configs/tokens";
import { bigMath } from "sdk/utils/bigmath";

import SEO from "components/Common/SEO";
import Footer from "components/Footer/Footer";
import PageTitle from "components/PageTitle/PageTitle";
import { MarketsList } from "components/Synthetics/MarketsList/MarketsList";

import { MarketsListV1 } from "./MarketsListV1";
import { OverviewCard } from "./OverviewCard";
import { StatsCard } from "./StatsCard";
import { useDashboardChainStatsMulticall } from "./useDashboardChainStatsMulticall";

import "./DashboardV2.css";

export const ACTIVE_CHAIN_IDS = [ARBITRUM, AVALANCHE];

export default function DashboardV2() {
  const { active, signer } = useWallet();
  const { chainId } = useChainId();

  const [tradePageVersion] = useTradePageVersion();

  const isLegacyChain = chainId === ARBITRUM || chainId === AVALANCHE;
  const isV1 = tradePageVersion === 1 && isLegacyChain;
  const isV2 = !isV1;

  const statsArbitrum = useDashboardChainStatsMulticall(ARBITRUM);
  const statsAvalanche = useDashboardChainStatsMulticall(AVALANCHE);

  const tokenWeightsArbitrum = statsArbitrum?.vault.totalTokenWeights;
  const tokenWeightsAvalanche = statsAvalanche?.vault.totalTokenWeights;
  const totalTokenWeights = chainId === ARBITRUM ? tokenWeightsArbitrum : tokenWeightsAvalanche;

  const glpTvlArbitrum = statsArbitrum?.glp.aum;
  const glpTvlAvalanche = statsAvalanche?.glp.aum;

  const glpSupplyArbitrum = statsArbitrum?.reader.tokenBalancesWithSupplies?.glpSupply;
  const glpSupplyAvalanche = statsAvalanche?.reader.tokenBalancesWithSupplies?.glpSupply;
  const glpSupply = chainId === ARBITRUM ? glpSupplyArbitrum : glpSupplyAvalanche;

  const { infoTokens: arbitrumInfoTokens } = useInfoTokens(
    signer,
    ARBITRUM,
    active && chainId === ARBITRUM,
    undefined,
    undefined
  );
  const { infoTokens: avalancheInfoTokens } = useInfoTokens(
    signer,
    AVALANCHE,
    active && chainId === AVALANCHE,
    undefined,
    undefined
  );
  const infoTokens = isLegacyChain ? (chainId === ARBITRUM ? arbitrumInfoTokens : avalancheInfoTokens) : {};

  const glpPriceArbitrum =
    glpTvlArbitrum !== undefined && glpTvlArbitrum > 0n && glpSupplyArbitrum !== undefined
      ? bigMath.mulDiv(glpTvlArbitrum, expandDecimals(1, GLP_DECIMALS), glpSupplyArbitrum)
      : expandDecimals(1, USD_DECIMALS);
  const glpPriceAvalanche =
    glpTvlAvalanche !== undefined && glpTvlAvalanche > 0n && glpSupplyAvalanche !== undefined
      ? bigMath.mulDiv(glpTvlAvalanche, expandDecimals(1, GLP_DECIMALS), glpSupplyAvalanche)
      : expandDecimals(1, USD_DECIMALS);
  const glpPrice = chainId === ARBITRUM ? glpPriceArbitrum : glpPriceAvalanche;

  const glpMarketCapArbitrum =
    glpSupplyArbitrum !== undefined
      ? bigMath.mulDiv(glpPriceArbitrum, glpSupplyArbitrum, expandDecimals(1, GLP_DECIMALS))
      : undefined;
  const glpMarketCapAvalanche =
    glpSupplyAvalanche !== undefined
      ? bigMath.mulDiv(glpPriceAvalanche, glpSupplyAvalanche, expandDecimals(1, GLP_DECIMALS))
      : undefined;
  const glpMarketCap = chainId === ARBITRUM ? glpMarketCapArbitrum : glpMarketCapAvalanche;

  const whitelistedTokens = isV1 ? getWhitelistedV1Tokens(chainId) ?? [] : [];
  const tokenList = whitelistedTokens.filter((t) => !t.isWrapped);

  let adjustedUsdgSupply = 0n;
  if (isV1) {
    for (let i = 0; i < tokenList.length; i++) {
      const token = tokenList[i];
      const tokenInfo = infoTokens[token.address];
      if (tokenInfo && tokenInfo.usdgAmount !== undefined) {
        adjustedUsdgSupply = adjustedUsdgSupply + tokenInfo.usdgAmount;
      }
    }
  }

  return (
    <SEO title={getPageTitle(t`Stats`)}>
      <div className="default-container DashboardV2 page-layout">
        <PageTitle
          title={t`Statistics Overview`}
          showNetworkIcon={false}
          isTop
          qa="dashboard-page"
          // subtitle={
          //   <div>
          //     <Trans>For detailed stats:</Trans>{" "}
          //     {chainId === ARBITRUM && <ExternalLink href="https://stats.gmx.io">V1 Analytics</ExternalLink>}
          //     {chainId === AVALANCHE && <ExternalLink href="https://stats.gmx.io/avalanche">V1 Analytics</ExternalLink>}
          //     {(chainId === ARBITRUM || chainId === AVALANCHE) && " | "}
          //     <ExternalLink href="https://dune.com/gmx-io/gmx-analytics">V2 Analytics</ExternalLink>
          //     {" | "}
          //     <Link className="link-underline" to="/monitor">
          //       V2 Pools Stats
          //     </Link>
          //     .
          //   </div>
          // }
          subtitle={<Trans>For more statistics details visit DefiLama</Trans>}
          hasBackButton
        />
        <div className="DashboardV2-content">
          <div className="DashboardV2-cards">
            <OverviewCard statsArbitrum={statsArbitrum} statsAvalanche={statsAvalanche} />
            <StatsCard statsArbitrum={statsArbitrum} statsAvalanche={statsAvalanche} />
          </div>
          {/* <DashboardPageTitle tradePageVersion={tradePageVersion} /> */}
          <div className="DashboardV2-token-cards">
            {/* <div className="stats-wrapper stats-wrapper--gmx">
              <GmxCard
                chainId={chainId}
                gmxPrice={gmxPrice}
                gmxPriceFromArbitrum={gmxPriceFromArbitrum}
                gmxPriceFromAvalanche={gmxPriceFromAvalanche}
                totalGmxSupply={totalGmxSupply}
                gmxMarketCap={gmxMarketCap}
                totalGmxInLiquidity={totalGmxInLiquidity}
              />
              {isV1 && (
                <GlpCard
                  chainId={chainId}
                  glpPrice={glpPrice}
                  glpSupply={glpSupply}
                  glpMarketCap={glpMarketCap}
                  adjustedUsdgSupply={adjustedUsdgSupply}
                />
              )}
              {isV2 && <GmCard />}
            </div> */}
            {isV1 && (
              <MarketsListV1
                chainId={chainId}
                infoTokens={infoTokens}
                totalTokenWeights={totalTokenWeights}
                adjustedUsdgSupply={adjustedUsdgSupply}
              />
            )}
            {isV2 && (
              <SyntheticsStateContextProvider skipLocalReferralCode={false} pageType="pools">
                <MarketsList />
              </SyntheticsStateContextProvider>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </SEO>
  );
}
